import { useQuery } from '@tanstack/react-query';
import { Check, Circle } from 'lucide-react';
import { checkPasswordPolicy, DEFAULT_PASSWORD_POLICY, type PasswordPolicy } from '@fas/validation';
import { apiRequest } from '@/lib/api';

export function usePasswordPolicy(): PasswordPolicy {
  const { data } = useQuery({
    queryKey: ['auth', 'password-policy'],
    queryFn: () => apiRequest<PasswordPolicy>('GET', '/auth/password-policy', { auth: false }).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  return data ?? DEFAULT_PASSWORD_POLICY;
}

/** Live checklist mirroring the server-side policy (the server remains authoritative). */
export function PasswordPolicyHints({ password, email, policy }: { password: string; email?: string | null; policy: PasswordPolicy }) {
  const rules: Array<[string, boolean]> = [
    [`At least ${policy.minLength} characters`, password.length >= policy.minLength],
    ...(policy.requireUppercase ? [['An uppercase letter', /[A-Z]/.test(password)] as [string, boolean]] : []),
    ...(policy.requireLowercase ? [['A lowercase letter', /[a-z]/.test(password)] as [string, boolean]] : []),
    ...(policy.requireDigit ? [['A number', /\d/.test(password)] as [string, boolean]] : []),
    ...(policy.requireSymbol ? [['A symbol', /[^A-Za-z0-9]/.test(password)] as [string, boolean]] : []),
  ];
  const otherProblems = password
    ? checkPasswordPolicy(password, policy, { email }).filter((p) => /common|email/.test(p))
    : [];
  return (
    <ul className="space-y-1 text-xs" aria-label="Password requirements">
      {rules.map(([label, ok]) => (
        <li key={label} className={ok ? 'flex items-center gap-1.5 text-emerald-800' : 'flex items-center gap-1.5 text-slate-600'}>
          {ok ? <Check className="size-3.5" aria-hidden /> : <Circle className="size-3.5" aria-hidden />}
          <span>
            {label}
            <span className="sr-only">{ok ? ' (met)' : ' (not met)'}</span>
          </span>
        </li>
      ))}
      {otherProblems.map((p) => (
        <li key={p} className="font-medium text-red-700">
          {p}
        </li>
      ))}
    </ul>
  );
}
